import { MessageModel } from 'types/chat/messages/message';
import { isChatReplyDebugEnabled, logChatReply, payloadLooksLikeReplyMetadata } from 'utils/debug-chat-reply';
import { IsJsonString } from 'utils/helpers';

/**
 * REST/socket may use different keys for the quoted message (replyMessage, parentMessage, …).
 * Normalizes nested object so UI (ReplyMessage, MessageWrapper) sees `Id` when API sends `id`.
 * Incoming replies often arrive as PascalCase (ReplyMessage) or as parent id + quoted text only.
 */
function getCaseInsensitive(m: Record<string, unknown>, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(m, key)) return m[key];
  const lower = key.toLowerCase();
  for (const k of Object.keys(m)) {
    if (k.toLowerCase() === lower) return m[k];
  }
  return undefined;
}

function parseNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number.parseInt(value.trim(), 10);
  return null;
}

function firstNonEmptyString(...vals: unknown[]): string | null {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

export function extractReplyPayloadFromApiMessage(raw: unknown): MessageModel | string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw;

  const m = raw as Record<string, unknown>;

  const nestedKeyCandidates = [
    'replyMessage',
    'ReplyMessage',
    'parentMessage',
    'ParentMessage',
    'quotedMessage',
    'QuotedMessage',
    'replyTo',
    'ReplyTo',
    'replyToMessage',
    'ReplyToMessage',
    'quoted',
    'Quoted',
    'messageReply',
    'MessageReply',
    'referencedMessage',
    'ReferencedMessage',
  ];

  let nested: unknown;
  for (const k of nestedKeyCandidates) {
    const v = getCaseInsensitive(m, k);
    if (v != null && v !== '') {
      nested = v;
      break;
    }
  }

  const ctx = getCaseInsensitive(m, 'context') as Record<string, unknown> | undefined;
  if (nested == null && ctx && typeof ctx === 'object') {
    nested =
      ctx.replyMessage ??
      ctx.parentMessage ??
      ctx.message ??
      getCaseInsensitive(ctx, 'replyMessage') ??
      getCaseInsensitive(ctx, 'parentMessage');
  }

  /* `reply` / `Reply` alone are ambiguous (flags, duplicate keys); only accept object or JSON string */
  if (nested == null) {
    const loose = getCaseInsensitive(m, 'reply');
    if (loose != null && loose !== '') {
      if (typeof loose === 'object' && !Array.isArray(loose)) {
        nested = loose;
      } else if (typeof loose === 'string' && IsJsonString(loose)) {
        try {
          nested = JSON.parse(loose);
        } catch {
          /* ignore */
        }
      }
    }
  }

  if (typeof nested === 'string') {
    if (IsJsonString(nested)) {
      try {
        nested = JSON.parse(nested);
      } catch {
        return nested as string;
      }
    } else {
      return nested;
    }
  }

  if (nested != null && typeof nested === 'object') {
    const o = nested as Record<string, unknown>;
    const id = o.Id ?? o.id ?? getCaseInsensitive(o, 'Id');
    const merged: MessageModel = {
      ...(o as unknown as MessageModel),
      Id:
        typeof id === 'number' && Number.isFinite(id)
          ? id
          : typeof id === 'string' && /^\d+$/.test(id)
            ? Number.parseInt(id, 10)
            : (o as MessageModel).Id ?? null,
    };
    return merged;
  }

  /* Flat / partial shapes common for incoming (id + preview, or WhatsApp context id only) */
  const pidRaw =
    getCaseInsensitive(m, 'parentMessageId') ??
    getCaseInsensitive(m, 'ParentMessageId') ??
    getCaseInsensitive(m, 'replyToMessageId') ??
    getCaseInsensitive(m, 'ReplyToMessageId') ??
    getCaseInsensitive(m, 'repliedToMessageId') ??
    getCaseInsensitive(m, 'RepliedToMessageId') ??
    getCaseInsensitive(m, 'parentId') ??
    getCaseInsensitive(m, 'ParentId') ??
    getCaseInsensitive(m, 'replyToId') ??
    getCaseInsensitive(m, 'ReplyToId') ??
    getCaseInsensitive(m, 'quotedMessageId') ??
    getCaseInsensitive(m, 'QuotedMessageId');

  const quotedBody = firstNonEmptyString(
    getCaseInsensitive(m, 'quotedMessageText'),
    getCaseInsensitive(m, 'QuotedMessageText'),
    getCaseInsensitive(m, 'quoteBody'),
    getCaseInsensitive(m, 'QuoteBody'),
    getCaseInsensitive(m, 'previewMessage'),
    getCaseInsensitive(m, 'PreviewMessage'),
    getCaseInsensitive(m, 'quotedMessageBody'),
    getCaseInsensitive(m, 'QuotedMessageBody'),
    ctx && typeof ctx === 'object'
      ? firstNonEmptyString(
          ctx.message as string,
          getCaseInsensitive(ctx, 'body') as string,
          getCaseInsensitive(ctx, 'text') as string,
        )
      : null,
  );

  const quotedType = firstNonEmptyString(
    getCaseInsensitive(m, 'quotedMessageType'),
    getCaseInsensitive(m, 'QuotedMessageType'),
    getCaseInsensitive(m, 'quoteFileType'),
    getCaseInsensitive(m, 'QuoteFileType'),
  );

  const ctxWamid =
    ctx && typeof ctx === 'object'
      ? firstNonEmptyString(
          typeof ctx.id === 'string' ? ctx.id : null,
          getCaseInsensitive(ctx, 'id') as string,
        )
      : null;

  const parentId = parseNumericId(pidRaw);

  if (parentId != null || quotedBody != null || ctxWamid != null) {
    const preview =
      quotedBody ??
      (parentId != null || ctxWamid != null ? 'הודעה לא זמינה' : null);
    if (preview == null) return null;

    const synthetic: MessageModel = {
      Id: parentId ?? undefined,
      Message: preview,
      FileType: (quotedType as MessageModel['FileType']) ?? 'text',
      Type: 'incoming',
      MessageId: ctxWamid ?? undefined,
      messageId: ctxWamid ?? undefined,
    };
    return synthetic;
  }

  return null;
}

/**
 * נכנסות: כמו `origin/web3-check` — קודם `replyMessage` / `ReplyMessage` מהשרת, רק אם אין — חילוץ מלא (הורה לפי id וכו').
 */
function normalizeDtoToMessageModel(o: Record<string, unknown>): MessageModel {
  return {
    Id: (o.Id ?? o.id ?? null) as number | null,
    Message: (o.Message ?? o.message ?? null) as string | null,
    MessageId: (o.MessageId ?? o.messageId ?? o.whatsappMessageId ?? null) as string | null,
    FileType: (o.FileType ?? o.fileType ?? o.type ?? null) as MessageModel['FileType'],
    File: (o.File ?? o.file ?? o.fileUrl ?? null) as string | null,
    DateCreate: (o.DateCreate ?? o.dateCreate ?? o.date ?? null) as string | null,
    Type: (o.Type ?? o.direction ?? null) as MessageModel['Type'],
    Status: (o.Status ?? o.status ?? null) as MessageModel['Status'],
    FormattedMessage: (o.FormattedMessage ?? o.formattedMessage ?? null) as string | null,
  };
}

export function extractReplyPayloadWithWeb3Fallback(m: Record<string, unknown>): MessageModel | string | null {
  const raw =
    m.replyMessage ??
    m.ReplyMessage ??
    m.replyToMessage ??
    m.ReplyToMessage ??
    m.Reply;

  let result: MessageModel | string | null;

  if (raw != null && raw !== '') {
    if (typeof raw === 'string') {
      result = extractReplyPayloadFromApiMessage(raw) ?? raw;
    } else if (typeof raw === 'object' && !Array.isArray(raw)) {
      const nested = extractReplyPayloadFromApiMessage(raw as Record<string, unknown>);
      result = nested ?? normalizeDtoToMessageModel(raw as Record<string, unknown>);
    } else {
      result = extractReplyPayloadFromApiMessage(m);
    }
  } else {
    result = extractReplyPayloadFromApiMessage(m);
  }

  if (isChatReplyDebugEnabled() && payloadLooksLikeReplyMetadata(m)) {
    const r = result && typeof result === 'object' ? (result as MessageModel) : null;
    logChatReply(result == null ? 'incoming:extract-missed' : 'incoming:extract-ok', {
      keys: Object.keys(m),
      resultNull: result == null,
      previewId: r?.Id,
      previewMessageId: r?.MessageId ?? r?.messageId,
    });
  }

  return result;
}
